import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  inject,
  input,
  OnInit,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BdShareablesService } from '@bindoc-projects/bindoc-permissions-lib/data';
import { BdShareableSelectService } from '../select/global-search/shareable-select.service';
import {
  ShareableObj,
  ShareableType,
} from '@bindoc-projects/resource-domain-lib';
import { Button } from 'primeng/button';
import { Popover } from 'primeng/popover';
import { take } from 'rxjs';

import { BdGrantsTabComponent } from './grants-tab/grants-tab.component';
import {
  BdWorkspaceNavigator,
  provideWorkspaceNavigation,
} from './navigation/workspace-navigator';
import { BdRecentTabComponent } from './recent-tab/recent-tab.component';
import { BdRecycleBinTabComponent } from './recycle-bin-tab/recycle-bin-tab.component';
import { BdScopeSidebarComponent } from './scope-sidebar/scope-sidebar.component';
import { BdShareablesTabComponent } from './shareables-tab/shareables-tab.component';
import {
  ItemNavigateEvent,
  NavigationScope,
  SidebarNavigation,
  ViewTab,
} from './shareables-overview.models';
import { BD_DIALOG_SERVICE_PROVIDER } from '@bindoc-projects/ui-lib/dialog/ui';

export type ShareablesLayoutMode = 'wide' | 'narrow';

interface ActiveSection {
  readonly icon: string;
  readonly label: string;
}

const NARROW_BREAKPOINT = 1024;

@Component({
  selector: 'bd-shareables-overview',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    BD_DIALOG_SERVICE_PROVIDER,
    // The page's navigation lives in the URL; the tabs below render it.
    ...provideWorkspaceNavigation(),
  ],
  template: `
    <div
      class="bd-shareables-overview flex flex-col flex-1 min-h-0 h-full"
      [attr.data-layout]="layoutMode()"
    >
      @if (layoutMode() === 'narrow') {
        <div
          class="flex items-center px-2 pt-2 border-b border-[var(--p-content-border-color)]"
        >
          <p-button
            severity="secondary"
            text
            [icon]="activeSection().icon"
            iconPos="left"
            [label]="activeSection().label"
            (onClick)="navPopover()?.toggle($event)"
            styleClass="max-w-full"
            data-test="shareables-overview-nav-toggle"
            ariaLabel="Navigation öffnen"
          >
            <i class="pi pi-chevron-down ml-2 text-xs opacity-70"></i>
          </p-button>
        </div>
        <p-popover
          #navPopoverRef
          styleClass="bd-shareables-overview__popover"
          [pt]="{ content: { style: 'padding: 0.25rem' } }"
        >
          <bd-scope-sidebar
            [bordered]="false"
            maxHeight="70vh"
            [activeTab]="activeTab()"
            [activeGroupId]="activeGroupScope().groupId"
            [forbiddenGroupIds]="forbiddenGroupIds()"
            (navigate)="onSidebarNavigate($event)"
          ></bd-scope-sidebar>
        </p-popover>
      }
      <div class="flex flex-1 min-h-0 overflow-hidden">
        @if (layoutMode() === 'wide') {
          <bd-scope-sidebar
            [activeTab]="activeTab()"
            [activeGroupId]="activeGroupScope().groupId"
            [forbiddenGroupIds]="forbiddenGroupIds()"
            (navigate)="onSidebarNavigate($event)"
          ></bd-scope-sidebar>
        }
        <div
          class="bd-shareables-overview__content flex flex-col flex-1 min-w-0 min-h-0 overflow-hidden p-4"
        >
          @switch (activeTab()) {
            @case ('recent') {
              <bd-recent-tab
                class="flex flex-col flex-auto min-h-0"
                [shareableType]="shareableType()"
                [showType]="!shareableType()"
                (itemNavigate)="onItemNavigate($event)"
              ></bd-recent-tab>
            }
            @case ('shareables') {
              <bd-shareables-tab
                class="flex flex-col flex-auto min-h-0"
                [userName]="userName()"
                [shareableType]="shareableType()"
                [showType]="!shareableType()"
                (itemNavigate)="onItemNavigate($event)"
              ></bd-shareables-tab>
            }
            @case ('group') {
              <bd-shareables-tab
                class="flex flex-col flex-auto min-h-0"
                [userName]="userName()"
                [shareableType]="shareableType()"
                [showType]="!shareableType()"
                (itemNavigate)="onItemNavigate($event)"
              ></bd-shareables-tab>
            }
            @case ('grants') {
              <bd-grants-tab
                class="flex flex-col flex-auto min-h-0"
                [shareableType]="shareableType()"
                [showType]="!shareableType()"
                (itemNavigate)="onItemNavigate($event)"
              ></bd-grants-tab>
            }
            @case ('recycle-bin') {
              <bd-recycle-bin-tab
                class="flex flex-col flex-auto min-h-0"
                [shareableType]="shareableType()"
                [showType]="!shareableType()"
              ></bd-recycle-bin-tab>
            }
          }
        </div>
      </div>
    </div>
  `,
  host: {
    class: 'flex flex-col h-full min-h-0',
  },
  styles: [
    // Layout uses Tailwind utilities on the host and elements; only the
    // container query (needs a named container) and the ::ng-deep rules that
    // reach into PrimeNG/child DOM remain as CSS here.
    `
      .bd-shareables-overview {
        container-type: inline-size;
        container-name: bd-shareables-overview;
      }
      @container bd-shareables-overview (max-width: 1023px) {
        .bd-shareables-overview__content {
          padding: 0.75rem;
        }
        :host ::ng-deep bd-folder-table .bd-folder-table__col--date {
          display: none;
        }
      }
      @container bd-shareables-overview (max-width: 639px) {
        .bd-shareables-overview__content {
          padding: 0.5rem;
        }
        :host ::ng-deep bd-folder-table .bd-folder-table__col--permissions {
          display: none;
        }
      }
    `,
  ],
  imports: [
    BdScopeSidebarComponent,
    BdRecentTabComponent,
    BdShareablesTabComponent,
    BdGrantsTabComponent,
    BdRecycleBinTabComponent,
    Button,
    Popover,
  ],
})
export class BdShareablesOverviewComponent implements OnInit {
  public readonly userName = input<string>('');
  public readonly shareableType = input<ShareableType | undefined>(undefined);
  /** Tab to open when the route carries no explicit `?tab=` query param. */
  public readonly initialTab = input<ViewTab>('recent');

  /**
   * Where the workspace is. The URL holds it; the navigator parses, resolves
   * and loads it — this component only picks the tab that renders it.
   */
  protected readonly navigator = inject(BdWorkspaceNavigator);

  public readonly activeTab = computed<ViewTab>(
    () => this.navigator.location().tab,
  );

  public readonly activeGroupScope = computed<NavigationScope>(() => {
    const location = this.navigator.location();
    if (location.tab !== 'group') {
      return { kind: 'user' };
    }
    const names = this.navigator.names();
    return {
      kind: 'group',
      groupId: location.groupId,
      groupName: names.groupName,
      companyName: names.companyName,
    };
  });

  /**
   * Groups that answered 403 while the user was browsing — both sidebar
   * instances (wide rail and responsive popover) lock them. Learned from
   * responses that already happened; nothing is probed up front.
   */
  public readonly forbiddenGroupIds = this.navigator.forbiddenGroupIds;

  public readonly activeSection = computed<ActiveSection>(() => {
    switch (this.activeTab()) {
      case 'recent':
        return { icon: 'pi pi-clock', label: 'Kürzlich verwendet' };
      case 'shareables':
        return { icon: 'pi pi-user', label: 'Eigene Ressourcen' };
      case 'grants':
        return { icon: 'pi pi-share-alt', label: 'Geteilt' };
      case 'recycle-bin':
        return { icon: 'pi pi-trash', label: 'Papierkorb' };
      case 'group':
        return {
          icon: 'pi pi-users',
          label: this.activeGroupScope().groupName ?? 'Gruppe',
        };
      default:
        return { icon: 'pi pi-bars', label: 'Navigation' };
    }
  });

  public readonly layoutMode = signal<ShareablesLayoutMode>('wide');

  public readonly navPopover = viewChild<Popover>('navPopoverRef');

  private readonly _resourcesService = inject(BdShareablesService);
  private readonly _selectService = inject(BdShareableSelectService);
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _host = inject(ElementRef<HTMLElement>);

  constructor() {
    afterNextRender(() => {
      if (typeof ResizeObserver === 'undefined') {
        return;
      }
      const host: HTMLElement = this._host.nativeElement;
      const apply = (width: number): void => {
        const mode: ShareablesLayoutMode =
          width < NARROW_BREAKPOINT ? 'narrow' : 'wide';
        if (mode !== this.layoutMode()) {
          this.layoutMode.set(mode);
        }
      };
      apply(host.getBoundingClientRect().width);
      const ro = new ResizeObserver((entries) => {
        const w = entries[0]?.contentRect.width ?? 0;
        apply(w);
      });
      ro.observe(host);
      this._destroyRef.onDestroy(() => ro.disconnect());
    });
  }

  ngOnInit(): void {
    this._selectService.selected$
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((shareable) => this.onShareableSelect(shareable));
    // Inputs are known now: a URL without `?tab=` opens the host's tab.
    this.navigator.connect(this.initialTab());
  }

  public onSidebarNavigate(nav: SidebarNavigation): void {
    this.navigator.selectTab(nav.tab, nav.groupId, nav.groupName);
    this.navPopover()?.hide();
  }

  public onItemNavigate(event: ItemNavigateEvent): void {
    this._selectService.navigate({
      id: event.id,
      type: event.type,
      name: '',
    } as ShareableObj);
  }

  /**
   * A shareable was picked elsewhere (global search): open the folder it lives
   * in, in whichever scope that folder belongs to. Without a readable folder it
   * can only have reached the user as a share.
   */
  public onShareableSelect(shareable: ShareableObj): void {
    const folderId = shareable.parentFolderId;
    if (!folderId) {
      this.navigator.selectTab('grants');
      return;
    }
    this._resourcesService
      .folder(folderId)
      .pipe(take(1))
      .subscribe({
        next: () => this.navigator.openLink({ folderId }),
        error: () => this.navigator.selectTab('grants'),
      });
  }
}
